import React, { useEffect, useState } from "react";
import "./staff-modules.css";
import API_URL from "../../apiConfig";

// Utility to deduplicate students by unique key (id/email)
function uniqueBy(arr, keyFn) {
  const seen = new Set();
  return arr.filter(item => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Utility to sort students by roll_no (numeric, ascending, blanks last)
function sortByRollNo(arr) {
  return [...arr].sort((a, b) => {
    const aRoll = parseInt(a.roll_no);
    const bRoll = parseInt(b.roll_no);
    if (isNaN(aRoll)) return 1;
    if (isNaN(bRoll)) return -1;
    return aRoll - bRoll;
  });
}

export default function Attendance({ token }) {
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [attendance, setAttendance] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [message, setMessage] = useState("");
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  // Fetch all batches on mount
  useEffect(() => {
    fetch(`${API_URL}/api/batches`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setBatches(data || []))
      .catch(() => setBatches([]));
  }, [token]);

  // Set default batch on load
  useEffect(() => {
    if (!selectedBatch && batches.length > 0) {
      setSelectedBatch(String(batches[0].id));
    }
  }, [batches, selectedBatch]);

  // Fetch subjects for selected batch
  useEffect(() => {
    if (!selectedBatch) {
      setSubjects([]);
      setSelectedSubject("");
      return;
    }
    setLoadingSubjects(true);
    fetch(`${API_URL}/api/subjects?batch_id=${selectedBatch}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Subjects API error");
        return res.json();
      })
      .then((data) => {
        setSubjects(data || []);
        setSelectedSubject("");
        setLoadingSubjects(false);
      })
      .catch(() => {
        setSubjects([]);
        setSelectedSubject("");
        setMessage("No subjects found for this batch.");
        setLoadingSubjects(false);
        setTimeout(() => setMessage(""), 3000);
      });
  }, [selectedBatch, token]);

  // Fetch students for selected batch
  useEffect(() => {
    if (!selectedBatch) {
      setAttendance([]);
      return;
    }
    setLoadingStudents(true);
    fetch(`${API_URL}/api/students?batch_id=${selectedBatch}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        // Deduplicate and sort students
        const uniqueStudents = sortByRollNo(
          uniqueBy(data || [], s => s.id || s.student_id || s.email)
        );
        setAttendance(
          uniqueStudents.map(s => ({
            id: s.id,
            name: s.name,
            roll_no: s.roll_no,
            course_year: s.course_year,
            session_year: s.session_year,
            status: "Present"
          }))
        );
        setLoadingStudents(false);
      })
      .catch(() => {
        setAttendance([]);
        setLoadingStudents(false);
      });
  }, [selectedBatch, token]);

  // Individual status change
  const handleStatusChange = (id, status) => {
    setAttendance(prev =>
      prev.map(s => (s.id === id ? { ...s, status } : s))
    );
  };

  // Mark all
  const handleMarkAll = (status) => {
    setAttendance(prev => prev.map(s => ({ ...s, status })));
  };

  // Submit attendance
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedBatch || !selectedSubject || !date) {
      setMessage("Please select batch, subject, and date.");
      return;
    }
    const payload = {
      batch_id: selectedBatch,
      subject_id: selectedSubject,
      date,
      attendance: attendance.map(s => ({
        student_id: s.id,
        status: s.status
      }))
    };
    fetch(`${API_URL}/api/attendance`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    })
      .then(res => res.json())
      .then(resData => {
        if (resData.error) {
          setMessage("Error marking attendance: " + resData.error);
        } else {
          setMessage("Attendance marked successfully!");
        }
        setTimeout(() => setMessage(""), 3000);
      })
      .catch(() => {
        setMessage("Error submitting attendance.");
        setTimeout(() => setMessage(""), 3000);
      });
  };

  return (
    <div className="module-wrapper">
      <h2>Attendance</h2>
      <p>
        Select batch and subject, then mark student attendance for the chosen date.
      </p>
      <form className="attendance-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <label>Date:</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
          <label>Batch:</label>
          <select
            value={selectedBatch}
            onChange={(e) => setSelectedBatch(e.target.value)}
            required
          >
            <option value="">-- Select Batch --</option>
            {batches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.start_year}-{b.end_year})
              </option>
            ))}
          </select>
          <label>Subject:</label>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            required
            disabled={!subjects.length || loadingSubjects}
          >
            <option value="">-- Select Subject --</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.subject_name}
              </option>
            ))}
          </select>
        </div>
        <div className="attendance-actions">
          <button type="button" onClick={() => handleMarkAll("Present")}>
            Mark All Present
          </button>
          <button type="button" onClick={() => handleMarkAll("Absent")}>
            Mark All Absent
          </button>
          <button type="button" onClick={() => handleMarkAll("Leave")}>
            Mark All Leave
          </button>
        </div>
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Roll No</th>
              <th>Name</th>
              <th>Class</th>
              <th>Batch</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loadingStudents ? (
              <tr>
                <td colSpan={5}>Loading students...</td>
              </tr>
            ) : attendance.length === 0 ? (
              <tr>
                <td colSpan={5}>No students found for this batch.</td>
              </tr>
            ) : (
              attendance.map((student) => (
                <tr key={student.id}>
                  <td style={{ textAlign: "center" }}>{student.roll_no || ""}</td>
                  <td>{student.name}</td>
                  <td style={{ textAlign: "center" }}>{student.course_year || ""}</td>
                  <td style={{ textAlign: "center" }}>{student.session_year || ""}</td>
                  <td>
                    <select
                      value={student.status}
                      onChange={(e) => handleStatusChange(student.id, e.target.value)}
                    >
                      <option>Present</option>
                      <option>Absent</option>
                      <option>Leave</option>
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <button className="primary-action" type="submit">
          Submit Attendance
        </button>
      </form>
      {message && <div className="form-success">{message}</div>}
    </div>
  );
}
